import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Text,
  Icon,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton
} from '@chakra-ui/react';
import { FiUploadCloud } from 'react-icons/fi';
import Select from 'react-select';
import axios from 'axios';

const UploadCertificate = ({ isOpenInitially = true, closeModal }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (isOpenInitially) {
      onOpen();
    }
  }, [isOpenInitially, onOpen]);

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [selectedAuthority, setSelectedAuthority] = useState(null);
  const [selectedValidity, setSelectedValidity] = useState(null);
  const [certificateOptions, setCertificateOptions] = useState([]);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [filteredAuthorities, setFilteredAuthorities] = useState([]);
  const [validityOptions, setValidityOptions] = useState([]);
  const fileInputRef = useRef(null);

  const [selectedSpecialization, setSelectedSpecialization] = useState(null);
  const [specializationOptions, setSpecializationOptions] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileURL, setFileURL] = useState('');

  const [isContinueDisabled, setIsContinueDisabled] = useState(false);

  const [alertMessage, setAlertMessage] = useState(null);

  useEffect(() => {
    const fetchCertificateSpecializationRelations = async () => {
      const ticketToken = sessionStorage.getItem('ticketToken');
      if (!ticketToken) {
        console.error('No ticket token found in sessionStorage');
        return;
      }
      // console.log('Ticket Token:', ticketToken);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-certificate-specialization-relations`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );

        console.log('Response data: ok ? :', response.data);

        // Process the response data
        const certificates = [];
        const authoritiesMap = {};

        response.data.data.forEach(item => {
          if (!certificates.some(cert => cert.value === item.certificate)) {
            certificates.push({
              value: item.certificate,
              label: item.certificate
            });
          }
          if (!authoritiesMap[item.certificate]) {
            authoritiesMap[item.certificate] = {};
          }
          if (!authoritiesMap[item.certificate][item.issuingAuthority]) {
            authoritiesMap[item.certificate][item.issuingAuthority] = [];
          }
          authoritiesMap[item.certificate][item.issuingAuthority].push(item.validityPeriod);
        });

        setSpecializationOptions(certificates);
        setAuthorityOptions(authoritiesMap);

      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchCertificateSpecializationRelations();
  }, []);

  useEffect(() => {
    if (selectedSpecialization) {
      const authorities = Object.keys(authorityOptions[selectedSpecialization] || {}).map(auth => ({
        value: auth,
        label: auth
      }));
      setFilteredAuthorities(authorities);
      setSelectedAuthority(null); // Clear the selected authority
      setSelectedValidity(null); // Clear the selected validity
    } else {
      setFilteredAuthorities([]);
    }
  }, [selectedSpecialization, authorityOptions]);

  useEffect(() => {
    if (selectedSpecialization && selectedAuthority) {
      const validities = authorityOptions[selectedSpecialization][selectedAuthority] || [];
      setValidityOptions(validities.map(validity => ({
        value: validity,
        label: validity
      })));
      setSelectedValidity(validities.length === 1 ? validities[0] : null); // Auto-select if only one option
    } else {
      setValidityOptions([]);
    }
  }, [selectedSpecialization, selectedAuthority, authorityOptions]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setFileURL(url);
    }
  };

  const handleFileUploadClick = () => {
    if (!selectedFile) {
      fileInputRef.current.click();
    }
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    fileInputRef.current.value = null; // Reset the file input
  };

  const [errorMessage, setErrorMessage] = useState('');

  const handleContinue = async () => {
    if (!selectedSpecialization || !selectedAuthority || (validityOptions.length > 1 && !selectedValidity)) {
      setErrorMessage('Please fill in all required fields.');
    } else {
      setErrorMessage('');
      setIsContinueDisabled(true);

      try {
        const ticketToken = sessionStorage.getItem('ticketToken');
        
        // Fetch the certificate specialization relations again to find the selected relation
        const relationsResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-certificate-specialization-relations`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );

        const selectedRelation = relationsResponse.data.data.find(item =>
          item.certificate === selectedSpecialization &&
          item.issuingAuthority === selectedAuthority &&
          item.validityPeriod === selectedValidity
        );

        if (!selectedRelation) {
          throw new Error("Selected certificate specialization relation not found");
        }

        // Check if the certificate is already stored
        const certificatesResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/applicant/get-applicant-certificates`,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`
            }
          }
        );

        const isAlreadyStored = certificatesResponse.data.data.some(cert => cert._id === selectedRelation._id);

        if (isAlreadyStored) {
          setAlertMessage("This certificate is already stored.");
          setIsContinueDisabled(false);
          return;
        }
        
        const formData = new FormData();
        formData.append('certificateId', selectedRelation._id);
        formData.append('certificateFile', selectedFile);
        // Proceed with uploading the certificate
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/applicant/upload-certificate`,
          
          formData,
          {
            headers: {
              Authorization: `Bearer ${ticketToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResponse.status === 200) {
          console.log("Certificate uploaded successfully");
          closeModal(selectedSpecialization);
        } else {
          console.error("Error uploading certificate:", uploadResponse.data.message);
          setAlertMessage("Error uploading certificate: " + uploadResponse.data.message);
        }
      } catch (error) {
        console.error("Error uploading certificate:", error);
        setAlertMessage("This certificate is already uploaded.please select another certificate.");
      } finally {
        setIsContinueDisabled(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); closeModal && closeModal(); }} isCentered>
      <ModalOverlay />
      <ModalContent mx="auto" my="auto" zIndex="10">
        <ModalHeader>Upload Certificate</ModalHeader>
        <ModalCloseButton onClick={() => { onClose(); closeModal && closeModal(); }} />
        <ModalBody>
          {alertMessage && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              <AlertTitle mr={2}>Error!</AlertTitle>
              <AlertDescription>{alertMessage}</AlertDescription>
              <CloseButton position="absolute" right="8px" top="8px" onClick={() => setAlertMessage(null)} />
            </Alert>
          )}

          <FormControl isRequired mb={6}>
            <FormLabel fontSize="xs" mb={2}>Certificate</FormLabel>
            <Select
              options={specializationOptions}
              className="basic-single-select"
              classNamePrefix="select"
              value={specializationOptions.find(option => option.value === selectedSpecialization)}
              onChange={(selectedOption) => {
                setSelectedSpecialization(selectedOption ? selectedOption.value : null);
              }}
              placeholder="Search and select specialization"
            />
          </FormControl>

          <FormControl isRequired mb={6}>
            <FormLabel fontSize="xs" mb={2}>Issuing Authority</FormLabel>
            <Select
              options={filteredAuthorities}
              className="basic-single-select"
              classNamePrefix="select"
              value={selectedAuthority ? filteredAuthorities.find(option => option.value === selectedAuthority) : null}
              onChange={(selectedOption) => {
                setSelectedAuthority(selectedOption ? selectedOption.value : null);
              }}
              placeholder="Search and select issuing authority"
            />
          </FormControl>

          {validityOptions.length > 1 && (
            <FormControl isRequired mb={6}>
              <FormLabel fontSize="xs" mb={2}>Validity Period</FormLabel>
              <Select
                options={validityOptions}
                className="basic-single-select"
                classNamePrefix="select"
                value={selectedValidity ? validityOptions.find(option => option.value === selectedValidity) : null}
                onChange={(selectedOption) => {
                  setSelectedValidity(selectedOption ? selectedOption.value : null);
                }}
                placeholder="Select validity period"
              />
            </FormControl>
          )}

          {errorMessage && <Text color="red.500" mb={4}>{errorMessage}</Text>}

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={!!selectedFile} // Disable input if a file is selected
          />

          <Box
            mt={6}
            border="2px dashed"
            borderColor="blue.500"
            borderRadius="md"
            p={6}
            textAlign="center"
            cursor={selectedFile ? "not-allowed" : "pointer"}
            width="100%"
            onClick={handleFileUploadClick}
          >
            {!selectedFile && <Icon as={FiUploadCloud} boxSize={10} color="blue.500" />}
            <Text mt={2} color="blue.500">
              {selectedFile ? "File selected" : "Click or drag the certificate file to upload."}
            </Text>
            {selectedFile && (
              <Text mt={2} color="gray.600">Selected file: {selectedFile.name}</Text>
            )}
          </Box>

          {selectedFile && (
            <Button mt={4} onClick={handleClearSelection}>
              Clear Selection
            </Button>
          )}

          <HStack mt={10} spacing={4} justifyContent="flex-end">
            <Button
              variant="outline"
              border="2px solid"
              borderColor="gray.200"
              color="black"
              height="40px"
              width="110px"
              fontSize="sm"
              _hover={{ bg: "none" }}
              onClick={() => { onClose(); closeModal && closeModal(); }}
            >
              Cancel
            </Button>

            <Button
              bg="blue.500"
              color="white"
              height="40px"
              width="110px"
              fontSize="sm"
              _hover={{ bg: "blue.500" }}
              onClick={handleContinue}
              disabled={isContinueDisabled}
            >
              Continue
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UploadCertificate;